import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { createPluginRegistration } from "@embedpdf/core";
import { EmbedPDF } from "@embedpdf/core/react";
import type {
  PDFContextState,
  PluginBatchRegistrations,
} from "@embedpdf/core/react";
import { usePdfiumEngine } from "@embedpdf/engines/react";

import {
  DocumentContent,
  DocumentManagerPluginPackage,
} from "@embedpdf/plugin-document-manager/react";
import type { InitialDocumentOptions } from "@embedpdf/plugin-document-manager/react";
import { Viewport, ViewportPluginPackage } from "@embedpdf/plugin-viewport/react";
import {
  Scroller,
  ScrollPluginPackage,
  ScrollStrategy,
  useScroll,
  useScrollCapability,
} from "@embedpdf/plugin-scroll/react";
import type { PageLayout } from "@embedpdf/plugin-scroll/react";
import { RenderLayer, RenderPluginPackage } from "@embedpdf/plugin-render/react";
import {
  InteractionManagerPluginPackage,
  PagePointerProvider,
} from "@embedpdf/plugin-interaction-manager/react";
import {
  SelectionLayer,
  SelectionPluginPackage,
  useSelectionCapability,
} from "@embedpdf/plugin-selection/react";
import {
  ZoomGestureWrapper,
  ZoomMode,
  ZoomPluginPackage,
  useZoom,
} from "@embedpdf/plugin-zoom/react";
import type { ZoomLevel } from "@embedpdf/plugin-zoom/react";
import { SearchLayer, SearchPluginPackage, useSearch } from "@embedpdf/plugin-search/react";
import { ThumbnailPluginPackage } from "@embedpdf/plugin-thumbnail/react";

import { PdfThumbnails } from "./pdf-thumbnails";
import { PdfToolbar } from "./pdf-toolbar";
import { PdfPageSurface } from "./pdf-page-surface";
import type {
  PdfRendererHandle,
  PdfRendererProps,
  PdfToolbarContext,
  PdfZoomLevel,
  ResolvedFeatures,
} from "./types";

import "./pdf-renderer.scss";

export type {
  PdfRendererProps,
  PdfRendererHandle,
  PdfRendererSource,
  PdfRendererFeatures,
  PdfToolbarContext,
  PdfPageInfo,
  PdfSearchSummary,
  PdfZoomLevel,
} from "./types";

const DEFAULT_FEATURES: ResolvedFeatures = {
  zoom: true,
  search: true,
  textSelection: false,
  thumbnails: true,
  pageNavigation: true,
  gestureZoom: true,
  formFields: true,
  annotations: true,
};

function clamp(n: number, min: number, max: number): number {
  return Math.min(Math.max(n, min), max);
}

function isBlob(value: unknown): value is Blob {
  return typeof Blob !== "undefined" && value instanceof Blob;
}

interface ResolvedSource {
  docs: InitialDocumentOptions[];
  /** Identity used to key the viewer so it remounts when the source changes. */
  key: string;
}

/** Latest-value refs for consumer callbacks, so effects don't refire on identity churn. */
interface PdfRendererCallbacks {
  onDocumentLoad: PdfRendererProps["onDocumentLoad"];
  onLayoutReady: PdfRendererProps["onLayoutReady"];
  onError: PdfRendererProps["onError"];
  onPageChange: PdfRendererProps["onPageChange"];
  onZoomChange: PdfRendererProps["onZoomChange"];
  onSearchResults: PdfRendererProps["onSearchResults"];
}

/**
 * Resolve every source shape that can be handled synchronously.
 * Returns `null` for `Blob`/`File`, which must be read asynchronously.
 */
function resolveSyncSource(
  src: PdfRendererProps["src"],
  password?: string,
): ResolvedSource | null {
  function withPassword<T extends object>(o: T): T {
    return password ? ({ ...o, password } as T) : o;
  }

  if (typeof src === "string") {
    return { docs: [withPassword({ url: src })], key: `url:${src}` };
  }
  if (isBlob(src)) {
    return null;
  }
  if (src instanceof ArrayBuffer) {
    return {
      docs: [withPassword({ buffer: src, name: "document.pdf" })],
      key: `buf:document.pdf:${src.byteLength}`,
    };
  }
  if (src instanceof Uint8Array) {
    const buffer = src.slice().buffer as ArrayBuffer;
    return {
      docs: [withPassword({ buffer, name: "document.pdf" })],
      key: `buf:document.pdf:${buffer.byteLength}`,
    };
  }
  if ("url" in src) {
    return { docs: [withPassword({ ...src })], key: `url:${src.url}` };
  }
  return {
    docs: [withPassword({ ...src })],
    key: `buf:${src.name}:${src.buffer.byteLength}`,
  };
}

export const PdfRenderer = forwardRef<PdfRendererHandle, PdfRendererProps>(
  function PdfRenderer(props, ref) {
    const {
      src,
      password,
      features,
      initialPage,
      page,
      initialZoom,
      scrollDirection = "vertical",
      pageGap,
      minZoom,
      maxZoom,
      zoomStep,
      thumbnailWidth = 120,
      thumbnailsPosition = "left",
      defaultThumbnailsOpen = false,
      wasmUrl,
      worker = true,
      toolbar,
      toolbarPosition = "top",
      renderLoading,
      renderError,
      renderEmpty,
      renderPageOverlay,
      className,
      style,
      height = 600,
      width = "100%",
      pageClassName,
      backgroundColor = "#f5f5f5",
      searchHighlightColor,
      searchActiveHighlightColor,
      onDocumentLoad,
      onLayoutReady,
      onError,
      onPageChange,
      onZoomChange,
      onSearchResults,
    } = props;

    const resolvedFeatures: ResolvedFeatures = { ...DEFAULT_FEATURES, ...features };

    // Read as primitives so the plugin registrations below don't rebuild (and
    // remount the viewer) on every render just because `features` is a new object.
    const renderForms = resolvedFeatures.formFields;
    const renderAnnotations = resolvedFeatures.annotations;

    const cbRef = useRef<PdfRendererCallbacks>({
      onDocumentLoad,
      onLayoutReady,
      onError,
      onPageChange,
      onZoomChange,
      onSearchResults,
    });
    cbRef.current = {
      onDocumentLoad,
      onLayoutReady,
      onError,
      onPageChange,
      onZoomChange,
      onSearchResults,
    };

    // --- Engine ---
    const engineConfig = useMemo(() => {
      const cfg: { wasmUrl?: string; worker?: boolean } = { worker };
      if (wasmUrl) {
        // The engine runs in a Web Worker, where a relative/root-relative URL can
        // resolve against the worker's own (blob:) base instead of the page origin
        // and silently fail to fetch. Resolve to an absolute URL up front so a
        // self-hosted path like "/pdfium.wasm" works the same as a CDN URL.
        cfg.wasmUrl =
          typeof window !== "undefined"
            ? new URL(wasmUrl, window.location.href).href
            : wasmUrl;
      }
      return cfg;
    }, [wasmUrl, worker]);

    const {
      engine,
      isLoading: engineLoading,
      error: engineError,
    } = usePdfiumEngine(engineConfig);

    // --- Source resolution (sync for URL/bytes, async for Blob/File) ---
    const syncSource = resolveSyncSource(src, password);
    const [asyncSource, setAsyncSource] = useState<ResolvedSource | null>(null);

    useEffect(() => {
      if (resolveSyncSource(src, password)) {
        setAsyncSource(null);
        return;
      }
      const blob = src as Blob;
      const name = (blob as File).name || "document.pdf";
      let cancelled = false;
      setAsyncSource(null);
      blob
        .arrayBuffer()
        .then((buffer) => {
          if (cancelled) return;
          const docs: InitialDocumentOptions[] = [
            password ? { buffer, name, password } : { buffer, name },
          ];
          setAsyncSource({ docs, key: `blob:${name}:${buffer.byteLength}` });
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          cbRef.current.onError?.(
            err instanceof Error ? err : new Error(String(err)),
          );
        });
      return () => {
        cancelled = true;
      };
    }, [src, password]);

    const resolved = syncSource ?? asyncSource;
    const resolvedKey = resolved?.key;

    // --- Plugins ---
    const plugins = useMemo<PluginBatchRegistrations>(() => {
      if (!resolved) return [];
      return [
        createPluginRegistration(DocumentManagerPluginPackage, {
          initialDocuments: resolved.docs,
        }),
        createPluginRegistration(ViewportPluginPackage),
        createPluginRegistration(ScrollPluginPackage, {
          defaultStrategy:
            scrollDirection === "horizontal"
              ? ScrollStrategy.Horizontal
              : ScrollStrategy.Vertical,
          ...(pageGap != null ? { defaultPageGap: pageGap } : {}),
        }),
        createPluginRegistration(RenderPluginPackage, {
          withForms: renderForms,
          withAnnotations: renderAnnotations,
        }),
        createPluginRegistration(InteractionManagerPluginPackage),
        createPluginRegistration(SelectionPluginPackage),
        createPluginRegistration(ZoomPluginPackage, {
          defaultZoomLevel: (initialZoom ?? ZoomMode.Automatic) as ZoomLevel,
          ...(minZoom != null ? { minZoom } : {}),
          ...(maxZoom != null ? { maxZoom } : {}),
          ...(zoomStep != null ? { zoomStep } : {}),
        }),
        createPluginRegistration(SearchPluginPackage),
        createPluginRegistration(ThumbnailPluginPackage, { width: thumbnailWidth }),
      ] as PluginBatchRegistrations;
      // resolved.docs is captured via resolvedKey; other deps drive plugin config.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      resolvedKey,
      scrollDirection,
      pageGap,
      initialZoom,
      minZoom,
      maxZoom,
      zoomStep,
      thumbnailWidth,
      renderForms,
      renderAnnotations,
    ]);

    // --- Status renderers ---
    const renderLoadingNode = (): React.ReactNode =>
      renderLoading ? renderLoading() : <DefaultStatus kind="loading" />;
    const renderErrorNode = (error: Error | null): React.ReactNode =>
      renderError ? (
        renderError(error)
      ) : (
        <DefaultStatus kind="error" message={error?.message} />
      );
    const renderEmptyNode = (): React.ReactNode =>
      renderEmpty ? renderEmpty() : <DefaultStatus kind="empty" />;

    let body: React.ReactNode;
    if (engineError) {
      body = <div className="pdf-renderer__state">{renderErrorNode(engineError)}</div>;
    } else if (!engine || engineLoading || !resolved) {
      body = <div className="pdf-renderer__state">{renderLoadingNode()}</div>;
    } else {
      body = (
        <EmbedPDF key={resolved.key} engine={engine} plugins={plugins}>
          {(state: PDFContextState) => {
            const activeId = state.activeDocumentId;
            if (!activeId) {
              return (
                <div className="pdf-renderer__state">
                  {state.isInitializing ? renderLoadingNode() : renderEmptyNode()}
                </div>
              );
            }
            return (
              <DocumentContent documentId={activeId}>
                {({ isLoading, isError }) => {
                  if (isError) {
                    return (
                      <div className="pdf-renderer__state">
                        {renderErrorNode(null)}
                      </div>
                    );
                  }
                  if (isLoading) {
                    return (
                      <div className="pdf-renderer__state">
                        {renderLoadingNode()}
                      </div>
                    );
                  }
                  return (
                    <PdfRendererLayout
                      documentId={activeId}
                      features={resolvedFeatures}
                      toolbar={toolbar}
                      toolbarPosition={toolbarPosition}
                      thumbnailWidth={thumbnailWidth}
                      thumbnailsPosition={thumbnailsPosition}
                      defaultThumbnailsOpen={defaultThumbnailsOpen}
                      pageClassName={pageClassName}
                      backgroundColor={backgroundColor}
                      searchHighlightColor={searchHighlightColor}
                      searchActiveHighlightColor={searchActiveHighlightColor}
                      initialPage={initialPage}
                      page={page}
                      renderPageOverlay={renderPageOverlay}
                      cbRef={cbRef}
                      handleRef={ref}
                    />
                  );
                }}
              </DocumentContent>
            );
          }}
        </EmbedPDF>
      );
    }

    const frameStyle: React.CSSProperties = { height, width, ...style };

    return (
      <div
        className={["pdf-renderer", className].filter(Boolean).join(" ")}
        style={frameStyle}
      >
        {body}
      </div>
    );
  },
);

interface PdfRendererLayoutProps {
  documentId: string;
  features: ResolvedFeatures;
  toolbar: PdfRendererProps["toolbar"];
  toolbarPosition: "top" | "bottom";
  thumbnailWidth: number;
  thumbnailsPosition: "left" | "right";
  defaultThumbnailsOpen: boolean;
  pageClassName?: string;
  backgroundColor: string;
  searchHighlightColor?: string;
  searchActiveHighlightColor?: string;
  initialPage?: number;
  page?: number;
  renderPageOverlay?: PdfRendererProps["renderPageOverlay"];
  cbRef: React.MutableRefObject<PdfRendererCallbacks>;
  handleRef: React.ForwardedRef<PdfRendererHandle>;
}

/** True when the focused element handles its own copy shortcut. */
function isEditableTarget(element: Element | null): boolean {
  if (!(element instanceof HTMLElement)) return false;
  if (element.isContentEditable) return true;
  const tag = element.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

/**
 * Makes Cmd/Ctrl+C copy the current PDF selection.
 *
 * Selected text lives in plugin state rather than the DOM — pages are PDFium
 * bitmaps — so the browser's own copy has nothing to act on and the shortcut
 * has to be bridged to the plugin explicitly. The listener is document-wide to
 * work without stealing focus, and stays inert unless this document has a
 * selection and nothing else (a real DOM selection, or a focused field) has a
 * stronger claim on the keystroke.
 *
 * @param props.documentId - Document whose selection should be copied
 * @returns Nothing; this component only installs the shortcut
 */
function PdfSelectionCopyShortcut({ documentId }: { documentId: string }) {
  const { provides: selection } = useSelectionCapability();
  const hasSelectionRef = useRef(false);

  useEffect(() => {
    if (!selection) return undefined;
    return selection
      .forDocument(documentId)
      .onSelectionChange((range) => {
        hasSelectionRef.current = range != null;
      });
  }, [selection, documentId]);

  useEffect(() => {
    if (!selection) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "c")
        return;
      if (!hasSelectionRef.current) return;
      if (isEditableTarget(document.activeElement)) return;
      if (window.getSelection()?.toString()) return;

      event.preventDefault();
      selection.forDocument(documentId).copyToClipboard();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [selection, documentId]);

  return null;
}

/**
 * Lives inside the `<EmbedPDF>` provider with a resolved `documentId`, so it can
 * use the per-document hooks (`useScroll`/`useZoom`/`useSearch`), wire the
 * imperative handle, emit callbacks and lay out the chrome around the viewport.
 */
function PdfRendererLayout(props: PdfRendererLayoutProps) {
  const {
    documentId,
    features,
    toolbar,
    toolbarPosition,
    thumbnailWidth,
    thumbnailsPosition,
    defaultThumbnailsOpen,
    pageClassName,
    backgroundColor,
    searchHighlightColor,
    searchActiveHighlightColor,
    initialPage,
    page,
    renderPageOverlay,
    cbRef,
    handleRef,
  } = props;

  const { state: scrollState, provides: scroll } = useScroll(documentId);
  const { provides: scrollCapability } = useScrollCapability();
  const { state: zoomState, provides: zoom } = useZoom(documentId);
  const { state: searchState, provides: search } = useSearch(documentId);

  const [thumbnailsOpen, setThumbnailsOpen] = useState(
    defaultThumbnailsOpen && features.thumbnails,
  );

  const totalPages = scrollState.totalPages;

  // Page positions are measured a beat after the document itself loads, and
  // the scroll plugin restores the stored offset the moment they are. A scroll
  // requested before that is dropped — yet the plugin already reports the
  // target page — so the viewer looks like it navigated while staying put.
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const wasLayoutReadyRef = useRef(false);
  const lastAppliedPageRef = useRef<number | undefined>(undefined);
  const didInitialScroll = useRef(false);
  /** Holds a navigation asked for before the layout could honour it. */
  const queuedPageRef = useRef<number | null>(null);

  useEffect(() => {
    setIsLayoutReady(false);
    wasLayoutReadyRef.current = false;
    lastAppliedPageRef.current = undefined;
    didInitialScroll.current = false;
    queuedPageRef.current = null;
    if (!scrollCapability) return undefined;
    return scrollCapability.onLayoutReady((event) => {
      if (event.documentId !== documentId) return;
      setIsLayoutReady(true);
      cbRef.current.onLayoutReady?.({ documentId });
    });
  }, [scrollCapability, documentId, cbRef]);

  const goToPage = useCallback(
    (page: number) => {
      if (!scroll || !isLayoutReady) {
        queuedPageRef.current = page;
        return;
      }
      const target = totalPages > 0 ? clamp(page, 1, totalPages) : page;
      scroll.scrollToPage({ pageNumber: target });
    },
    [scroll, totalPages, isLayoutReady],
  );

  useEffect(() => {
    if (!isLayoutReady) return;
    const queued = queuedPageRef.current;
    if (queued == null) return;
    queuedPageRef.current = null;
    goToPage(queued);
  }, [isLayoutReady, goToPage]);

  const nextPage = useCallback(() => {
    scroll?.scrollToNextPage();
  }, [scroll]);
  const previousPage = useCallback(() => {
    scroll?.scrollToPreviousPage();
  }, [scroll]);
  const zoomIn = useCallback(() => {
    zoom?.zoomIn();
  }, [zoom]);
  const zoomOut = useCallback(() => {
    zoom?.zoomOut();
  }, [zoom]);
  const setZoom = useCallback(
    (level: PdfZoomLevel) => {
      zoom?.requestZoom(level as ZoomLevel);
    },
    [zoom],
  );
  const runSearch = useCallback(
    (query: string) => {
      if (!search) return;
      if (query.trim().length > 0) {
        search.startSearch();
        search.searchAllPages(query);
      } else {
        search.stopSearch();
      }
    },
    [search],
  );
  const nextSearchResult = useCallback(() => {
    search?.nextResult();
  }, [search]);
  const previousSearchResult = useCallback(() => {
    search?.previousResult();
  }, [search]);
  const clearSearch = useCallback(() => {
    search?.stopSearch();
  }, [search]);
  const toggleThumbnails = useCallback(() => {
    setThumbnailsOpen((v) => !v);
  }, []);

  useImperativeHandle(
    handleRef,
    () => ({
      goToPage,
      nextPage,
      previousPage,
      zoomIn,
      zoomOut,
      setZoom,
      search: runSearch,
      nextSearchResult,
      previousSearchResult,
      clearSearch,
      getState: () => ({
        currentPage: scrollState.currentPage,
        totalPages: scrollState.totalPages,
        zoom: zoomState.currentZoomLevel,
      }),
    }),
    [
      goToPage,
      nextPage,
      previousPage,
      zoomIn,
      zoomOut,
      setZoom,
      runSearch,
      nextSearchResult,
      previousSearchResult,
      clearSearch,
      scrollState.currentPage,
      scrollState.totalPages,
      zoomState.currentZoomLevel,
    ],
  );

  // --- Consumer callbacks ---
  useEffect(() => {
    cbRef.current.onDocumentLoad?.({ documentId });
  }, [documentId, cbRef]);
  useEffect(() => {
    cbRef.current.onPageChange?.({
      currentPage: scrollState.currentPage,
      totalPages: scrollState.totalPages,
    });
  }, [scrollState.currentPage, scrollState.totalPages, cbRef]);
  useEffect(() => {
    cbRef.current.onZoomChange?.({
      zoomLevel: zoomState.zoomLevel as PdfZoomLevel,
      currentZoomLevel: zoomState.currentZoomLevel,
    });
  }, [zoomState.zoomLevel, zoomState.currentZoomLevel, cbRef]);
  useEffect(() => {
    cbRef.current.onSearchResults?.({
      total: searchState.total,
      activeResultIndex: searchState.activeResultIndex,
      query: searchState.query,
      loading: searchState.loading,
    });
  }, [
    searchState.total,
    searchState.activeResultIndex,
    searchState.query,
    searchState.loading,
    cbRef,
  ]);

  // --- Initial page (once layout metrics are available) ---
  // Skipped when the controlled `page` prop is in use, which then governs navigation.
  useEffect(() => {
    if (didInitialScroll.current) return;
    if (page == null && initialPage && scroll && isLayoutReady && totalPages > 0) {
      didInitialScroll.current = true;
      scroll.scrollToPage({ pageNumber: clamp(initialPage, 1, totalPages) });
    }
  }, [initialPage, page, scroll, isLayoutReady, totalPages]);

  // --- Controlled page: navigate when the prop changes or layout becomes ready ---
  useEffect(() => {
    if (page == null || !scroll || !isLayoutReady || totalPages <= 0) {
      wasLayoutReadyRef.current = isLayoutReady;
      return;
    }
    const layoutBecameReady = isLayoutReady && !wasLayoutReadyRef.current;
    wasLayoutReadyRef.current = isLayoutReady;
    if (page === lastAppliedPageRef.current && !layoutBecameReady) return;
    lastAppliedPageRef.current = page;
    scroll.scrollToPage({ pageNumber: clamp(page, 1, totalPages) });
  }, [page, scroll, isLayoutReady, totalPages]);

  // --- Scroll to the active search match ---
  // EmbedPDF's search plugin only tracks the active result index; it does not
  // move the viewport. Bridge it to the scroll plugin here. Deduped by match
  // identity so progressive result streaming doesn't re-scroll to the same hit.
  const lastSearchScrollRef = useRef<string | null>(null);
  useEffect(() => {
    if (!scroll) return;
    const idx = searchState.activeResultIndex;
    if (idx < 0) {
      lastSearchScrollRef.current = null;
      return;
    }
    const result = searchState.results[idx];
    if (!result) return;
    const rect = result.rects[0];
    if (!rect) return;
    const key = `${result.pageIndex}:${result.charIndex}`;
    if (key === lastSearchScrollRef.current) return;
    lastSearchScrollRef.current = key;
    scroll.scrollToPage({
      pageNumber: result.pageIndex + 1,
      pageCoordinates: { x: rect.origin.x, y: rect.origin.y },
      behavior: "smooth",
      alignY: 30,
    });
  }, [scroll, searchState.activeResultIndex, searchState.results]);

  const toolbarCtx: PdfToolbarContext = {
    currentPage: scrollState.currentPage,
    totalPages: scrollState.totalPages,
    zoom: zoomState.currentZoomLevel,
    zoomLevel: zoomState.zoomLevel,
    goToPage,
    nextPage,
    previousPage,
    zoomIn,
    zoomOut,
    setZoom,
    search: runSearch,
    nextSearchResult,
    previousSearchResult,
    clearSearch,
    searchResults: {
      total: searchState.total,
      activeResultIndex: searchState.activeResultIndex,
      query: searchState.query,
      loading: searchState.loading,
    },
    thumbnailsOpen,
    toggleThumbnails,
    features,
  };

  let toolbarNode: React.ReactNode = null;
  if (toolbar === false) {
    toolbarNode = null;
  } else if (toolbar == null) {
    toolbarNode = <PdfToolbar ctx={toolbarCtx} />;
  } else if (typeof toolbar === "function") {
    toolbarNode = toolbar(toolbarCtx);
  } else {
    toolbarNode = toolbar;
  }

  const renderPage = (layout: PageLayout): React.ReactNode => {
    const { width, height, pageIndex, pageNumber } = layout;
    return (
      <PdfPageSurface
        key={`${documentId}:${pageIndex}`}
        className={["pdf-renderer__page", pageClassName].filter(Boolean).join(" ")}
        style={{ width, height, position: "relative" }}
      >
        {(isRendered) => (
          <>
            <RenderLayer
              documentId={documentId}
              pageIndex={pageIndex}
              data-pdf-page-image="true"
              style={{ width, height, display: "block" }}
            />
            {features.search && (
              <SearchLayer
                documentId={documentId}
                pageIndex={pageIndex}
                style={{ position: "absolute", top: 0, left: 0 }}
                {...(searchHighlightColor
                  ? { highlightColor: searchHighlightColor }
                  : {})}
                {...(searchActiveHighlightColor
                  ? { activeHighlightColor: searchActiveHighlightColor }
                  : {})}
              />
            )}
            {features.textSelection && (
              <PagePointerProvider
                documentId={documentId}
                pageIndex={pageIndex}
                style={{ position: "absolute", inset: 0 }}
              >
                <SelectionLayer documentId={documentId} pageIndex={pageIndex} />
              </PagePointerProvider>
            )}
            {renderPageOverlay && (
              <div
                className="pdf-renderer__page-overlay"
                hidden={!isRendered}
                style={{ position: "absolute", inset: 0 }}
              >
                {renderPageOverlay({
                  pageIndex,
                  pageNumber,
                  width,
                  height,
                  pdfWidth: width / zoomState.currentZoomLevel,
                  pdfHeight: height / zoomState.currentZoomLevel,
                })}
              </div>
            )}
          </>
        )}
      </PdfPageSurface>
    );
  };

  const scroller = <Scroller documentId={documentId} renderPage={renderPage} />;
  const viewportChildren =
    features.zoom && features.gestureZoom ? (
      <ZoomGestureWrapper documentId={documentId} style={{ minHeight: "100%" }}>
        {scroller}
      </ZoomGestureWrapper>
    ) : (
      scroller
    );

  const thumbnailsNode =
    features.thumbnails && thumbnailsOpen ? (
      <PdfThumbnails
        documentId={documentId}
        width={thumbnailWidth}
        currentPage={scrollState.currentPage}
        onSelect={goToPage}
      />
    ) : null;

  return (
    <div className="pdf-renderer__frame">
      {features.textSelection && (
        <PdfSelectionCopyShortcut documentId={documentId} />
      )}
      {toolbarPosition === "top" && toolbarNode != null && (
        <div className="pdf-renderer__toolbar-slot">{toolbarNode}</div>
      )}
      <div className="pdf-renderer__body">
        {thumbnailsPosition === "left" && thumbnailsNode}
        <Viewport
          documentId={documentId}
          className="pdf-renderer__viewport"
          style={{ backgroundColor }}
        >
          {viewportChildren}
        </Viewport>
        {thumbnailsPosition === "right" && thumbnailsNode}
      </div>
      {toolbarPosition === "bottom" && toolbarNode != null && (
        <div className="pdf-renderer__toolbar-slot">{toolbarNode}</div>
      )}
    </div>
  );
}

function DefaultStatus({
  kind,
  message,
}: {
  kind: "loading" | "error" | "empty";
  message?: string;
}) {
  if (kind === "loading") {
    return (
      <div className="pdf-renderer__status pdf-renderer__status--loading">
        <span className="pdf-renderer__spinner" aria-hidden="true" />
        <span>Loading PDF…</span>
      </div>
    );
  }
  if (kind === "error") {
    return (
      <div className="pdf-renderer__status pdf-renderer__status--error">
        <span>{message || "Unable to load PDF."}</span>
      </div>
    );
  }
  return (
    <div className="pdf-renderer__status pdf-renderer__status--empty">
      <span>No document to display.</span>
    </div>
  );
}

export default PdfRenderer;
